import { Horizon } from '@stellar/stellar-sdk';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { LS_FREIGHTER_ACCOUNT_CHANGE_IMMEDIATELY } from 'constants/local-storage';
import { AppRoutes } from 'constants/routes';
import { LOBSTR_CONNECTION_KEY } from 'constants/session-storage';
import { FREIGHTER_ID } from 'constants/wallets';

import PromisedTimeout from 'helpers/promised-timeout';

import { useAssetsActions } from 'store/assetsStore/useAssetsStore';
import { clearSavedAuthData, getSavedAuthData } from 'store/authStore/auth-helpers';
import { LoginTypes } from 'store/authStore/types';
import useAuthStore from 'store/authStore/useAuthStore';

import { AssetsEvent } from 'services/assets.service';
import { AuthEvent } from 'services/auth/events/events';
import {
    AssetsService,
    AuthService,
    ModalService,
    SorobanService,
    StellarService,
    ToastService,
} from 'services/globalServices';
import { StellarEvents } from 'services/stellar/events/events';

import FreighterAccountChangedModal from 'web/modals/FreighterAccountChangedModal';

import { useSkipFirstRender } from './useSkipFirstRender';

const UnfundedErrors = ['Request failed with status code 404', 'Not Found'];

export default function useGlobalSubscriptions(): void {
    const {
        login,
        logout,
        resolveFederation,
        account,
        isLogged,
        updateAccount,
        loginErrorText,
        clearLoginError,
        enableRedirect,
        setSessionRestorePending,
    } = useAuthStore();

    const { processNewAssets } = useAssetsActions();

    const accountRef = useRef(account);
    const accountId = isLogged ? account?.accountId() : undefined;

    useLayoutEffect(() => {
        // Stream callbacks can arrive between a logout commit and effect cleanup.
        accountRef.current = isLogged ? account : null;
    }, [account, isLogged]);

    useEffect(() => {
        const saved = getSavedAuthData();
        const { pubKey, loginType, walletKitId, lobstrConnectionKey, bipPath } = saved;
        let active = true;
        const isCurrent = () => {
            const current = getSavedAuthData();
            return (
                active &&
                current.pubKey === pubKey &&
                current.loginType === loginType &&
                current.walletKitId === walletKitId &&
                current.bipPath === bipPath
            );
        };

        const restore = async () => {
            if (!loginType) return;
            if (loginType === LoginTypes.secret) {
                clearSavedAuthData();
                return;
            }
            if (loginType === LoginTypes.walletKit) {
                const wallet = await AuthService.getWalletKit();
                if (!isCurrent()) return;
                if (!wallet.restoreLogin(walletKitId, pubKey)) {
                    clearSavedAuthData();
                    return;
                }
                login({ pubKey, loginType, walletKitId });
                return;
            }
            if (loginType === LoginTypes.walletConnect) {
                // The dedicated WebView page owns its auto-connect flow.
                if (
                    window.location.pathname.replace(/\/$/, '') ===
                    AppRoutes.page.walletConnect.replace(/\/$/, '')
                )
                    return;
                const wallet = await AuthService.getWalletConnect();
                if (isCurrent()) await wallet.onAppStart(false);
                return;
            }
            if (loginType === LoginTypes.ledger) {
                const wallet = await AuthService.getLedger();
                if (isCurrent()) await wallet.reLogin(bipPath, pubKey);
                return;
            }
            if (loginType === LoginTypes.lobstr) {
                sessionStorage.setItem(LOBSTR_CONNECTION_KEY, lobstrConnectionKey);
            }
            login({ pubKey, loginType });
        };

        // Include lazy SDK loading in the UI's restoration phase. The login action
        // still owns isLoginPending while the account response is in flight.
        const hasRestorableSession = Boolean(loginType && loginType !== LoginTypes.secret);
        setSessionRestorePending(hasRestorableSession);
        void restore()
            .catch(() => {
                if (isCurrent()) {
                    ToastService.showErrorToast(
                        'Could not restore your wallet. Please sign in again.',
                    );
                }
            })
            .finally(() => {
                if (active && hasRestorableSession) setSessionRestorePending(false);
            });
        return () => {
            active = false;
            if (hasRestorableSession) setSessionRestorePending(false);
        };
    }, []);

    const changeFreighterAccount = async (publicKey: string): Promise<void> => {
        ModalService.closeAllModals();
        await PromisedTimeout(500);
        const path = `${location.pathname}${location.search}`;
        logout();

        await PromisedTimeout(500);

        enableRedirect(path);
        login({ pubKey: publicKey, loginType: LoginTypes.walletKit, walletKitId: FREIGHTER_ID });
        (await AuthService.getWalletKit()).restoreLogin(FREIGHTER_ID, publicKey);
    };

    useEffect(() => {
        const unsub = AuthService.event.sub(event => {
            // LOGIN
            if (event.type === AuthEvent.ledgerLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.ledger,
                    bipPath: event.bipPath,
                });
            }
            if (event.type === AuthEvent.walletConnectLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.walletConnect,
                    metadata: event.metadata,
                    topic: event.topic,
                });
            }
            if (event.type === AuthEvent.lobstrExtensionLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.lobstr,
                });
            }

            if (event.type === AuthEvent.walletKitLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.walletKit,
                    walletKitId: event.id,
                });
            }

            // LOGOUT
            if (event.type === AuthEvent.walletConnectLogout) {
                ModalService.closeAllModals();
                PromisedTimeout(500).then(() => {
                    logout();
                });
            }
            if (event.type === AuthEvent.ledgerLogout) {
                logout();
            }
            if (event.type === AuthEvent.walletKitLogout) {
                ModalService.closeAllModals();
                logout();
            }

            // OTHER

            if (event.type === AuthEvent.walletKitAccountChanged) {
                const defaultChoice = JSON.parse(
                    localStorage.getItem(LS_FREIGHTER_ACCOUNT_CHANGE_IMMEDIATELY),
                );

                if (defaultChoice === null) {
                    ModalService.closeAllModals();
                    ModalService.openModal(FreighterAccountChangedModal, {
                        publicKey: event.publicKey,
                    });
                    return;
                }

                if (defaultChoice) {
                    changeFreighterAccount(event.publicKey);
                }
            }
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        const unsub = AssetsService.event.sub(event => {
            if (event.type === AssetsEvent.newAssets) {
                processNewAssets(event.payload);
            }
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        if (loginErrorText) {
            ToastService.showErrorToast(
                UnfundedErrors.includes(loginErrorText)
                    ? 'Deposit at least 1 XLM to activate the account first.'
                    : loginErrorText,
            );
            clearLoginError();
        }
        if (UnfundedErrors.includes(loginErrorText)) {
            AuthService.logoutWalletConnect();
            logout();
        }
    }, [loginErrorText]);

    useSkipFirstRender(() => {
        if (isLogged) {
            if (account.home_domain) {
                resolveFederation(account.home_domain, account.accountId());
            }
            ToastService.showSuccessToast('Logged in');
        } else {
            SorobanService.connection.logoutWithSecret();
            AuthService.logoutWalletKit();
            ToastService.showSuccessToast('Logged out');
        }
    }, [isLogged]);

    useEffect(() => {
        if (!accountId) return;

        StellarService.account.startAccountStream(accountId);
        return () => StellarService.account.closeAccountStream();
    }, [accountId]);

    useEffect(() => {
        if (account) {
            processNewAssets(account.getClassicTokens());
            return;
        }
    }, [account]);

    useEffect(() => {
        const unsub = StellarService.event.sub(({ type, account: newAccount }) => {
            const currentAccount = accountRef.current;

            // Ignore queued stream/transaction updates after logout or a wallet switch.
            if (
                !currentAccount ||
                !newAccount ||
                currentAccount.accountId() !== newAccount.account_id
            ) {
                return;
            }

            if (
                type === StellarEvents.handleAccountUpdate ||
                (type === StellarEvents.accountStream &&
                    StellarService.account.balancesHasChanges(
                        currentAccount.balances as Horizon.HorizonApi.BalanceLineAsset[],
                        newAccount.balances as Horizon.HorizonApi.BalanceLineAsset[],
                    ))
            ) {
                updateAccount(newAccount, currentAccount.authType);
            }
        });

        return () => unsub();
    }, []);
}
